import { Row, Col, Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';

import { ReactComponent as Logout } from 'src/assets/icons/logout.svg';
import AtomicaLogo from 'src/components/Logo/AtomicaLogo';
import {
  FaUserCircle,
  FaMoon,
  FaSun,
  LuUsers,
  LuSettings2,
  LuBuilding2,
  LuFileWarning,
  LuShield,
} from 'src/components/AdminPageLayout/icons';
import LogsTrigger from './LogsTrigger';
import ProfileDropdown from './ProfileDropdown';

import { goToLogout } from 'src/services/navigation';
import { useTranslation } from 'src/hooks/useTranslation';
import { ROUTES } from 'src/services/navigation/constants';
import useGetUserQuery from 'src/hooks/useGetUserQuery';
import useIsAdmin from 'src/hooks/useIsAdmin';
import { useTheme } from 'src/contexts/ThemeContext';
import { buildTestId } from 'src/utils/testId';

import * as styles from './NavBar.module.scss';

const TABS = {
  admin: {
    tabs: [
      { name: 'Users', path: ROUTES.USERS, base: ROUTES.USERS, icon: LuUsers },
      { name: 'Settings', path: ROUTES.RESOURCES, base: ROUTES.RESOURCES, icon: LuSettings2 },
      {
        name: 'Client Management',
        path: ROUTES.CLIENT_MANAGEMENT,
        base: ROUTES.CLIENT_MANAGEMENT,
        icon: LuBuilding2,
      },
      {
        name: 'Error Messages',
        path: ROUTES.ERROR_MESSAGES,
        base: ROUTES.ERROR_MESSAGES,
        icon: LuFileWarning,
      },
    ],
  },
  account: {
    tabs: [
      { name: 'Overview', path: ROUTES.ACCOUNT, base: ROUTES.ACCOUNT, icon: LuUsers },
      { name: 'Profile', path: ROUTES.PROFILE, base: ROUTES.PROFILE, icon: FaUserCircle },
    ],
  },
};

const getNavTabs = (pathname, isAdmin) => {
  const portalName = pathname?.split('/')[1];

  if (portalName === 'admin') {
    return isAdmin ? TABS.admin : TABS.account;
  }

  if (portalName === 'account') {
    const accountTabs = { ...TABS.account };
    if (isAdmin) {
      accountTabs.tabs = [
        ...accountTabs.tabs,
        { name: 'Admin', path: ROUTES.USERS, base: '/admin', icon: LuShield },
      ];
    }
    return accountTabs;
  }

  return TABS.account;
};

const Navbar = ({ hideTabs = false }) => {
  const { data: user } = useGetUserQuery() || {};
  const isAdmin = useIsAdmin();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const hasName = user?.given_name || user?.FirstName;
  const navbarTabs = getNavTabs(location.pathname, isAdmin);

  const { t } = useTranslation();

  return (
    <Row className={styles.navbar}>
      <Col xs={3} className={styles.logoCol}>
        <Link className={styles.logoLink} to={ROUTES.ACCOUNT}>
          <AtomicaLogo data-testid="app-logo" glowInDarkMode />
        </Link>
      </Col>

      <Col xs={5} className={styles.tabsCol}>
        <Nav className={styles.navTabs} data-testid="tabs">
          {!hideTabs &&
            navbarTabs?.tabs.map((tab, idx) => {
              const isActive = location.pathname.startsWith(tab.base);
              return (
                <Nav.Item key={idx} data-testid={buildTestId('tab-item', idx + 1)}>
                  <Nav.Link
                    as={Link}
                    to={tab.path}
                    className={styles.navLink}
                    active={isActive}
                    title={t(tab.name)}
                    aria-label={t(tab.name)}
                  >
                    <span className={styles.navLinkIcon} aria-hidden="true">
                      {tab.icon ? <tab.icon /> : null}
                    </span>
                    <span className={styles.navLinkText}>{t(tab.name)}</span>
                  </Nav.Link>
                </Nav.Item>
              );
            })}
        </Nav>
      </Col>

      <Col xs={4} className={styles.rightMenu}>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.themeToggle}
            onClick={toggleTheme}
            title={theme === 'dark' ? t('Switch to light mode') : t('Switch to dark mode')}
            aria-label={theme === 'dark' ? t('Switch to light mode') : t('Switch to dark mode')}
          >
            {theme === 'dark' ? (
              <FaSun className={styles.themeIcon} />
            ) : (
              <FaMoon className={styles.themeIcon} />
            )}
          </button>
          <LogsTrigger />
        </div>

        <Nav className={styles.nav}>
          {hasName ? (
            <ProfileDropdown user={user}>
              <Nav.Item className={styles.avatar} data-testid="profile-img">
                <FaUserCircle data-testid="profile-logo" className={styles.avatarIcon} />
              </Nav.Item>
              <Nav.Item className={styles.username} data-testid="profile-section">
                <div className={styles.welcomeText}>{t('Welcome')}</div>
                <div data-testid="profile-name" className={styles.profileName}>
                  {user?.given_name
                    ? `${user?.given_name || ''} ${user?.family_name || ''}`.trim()
                    : user?.FirstName
                      ? `${user?.FirstName || ''} ${user?.LastName || ''}`.trim()
                      : user?.Value}
                </div>
              </Nav.Item>
            </ProfileDropdown>
          ) : (
            <Nav.Item className={styles.avatar} data-testid="profile-img" />
          )}

          <Nav.Item
            className={styles.logout}
            onClick={goToLogout}
            data-testid="logout-btn"
            role="button"
          >
            <Logout />
          </Nav.Item>
        </Nav>
      </Col>
    </Row>
  );
};

export default Navbar;
